import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedTake,
  updateSelectedTake,
} from "../../app/slices/TrainingSlice";
import PiConfirmationDialog from "../../components/PiConfirmationDialog";
import PiPopoverMenu from "../../components/PiPopoverMenu";
import Take3D from "../../models/Take3D";
import PiMenuButton from "../menu/PiMenuButton";
import classNames from "classnames";
import InteractionContextMenu from "../interaction_context_menu/InteractionContextMenu";
import { selectErrors, selectWarnings } from "../../app/slices/ValidationSlice";
import { getTake3DSequence } from "../../models/Take3D";
import InfoIcon from "@mui/icons-material/Info";
import ErrorIcon from "@mui/icons-material/Error";
import { countDisabledInteractions } from "../../helpers/DisabledInteractionsHelper";
import DisabledInteractionsBadge from "../training_timeline/DisabledInteractionsBadge";
import { useDeleteTake3D } from "./useDeleteTake3D";

export const Take3DCardHeader = forwardRef<
  HTMLDivElement,
  {
    take: Take3D;
    isSelected: boolean;
  }
>(({ take, isSelected, ...props }, ref) => {
  const dispatch = useDispatch();
  const { deleteTake3D } = useDeleteTake3D();
  const allWarnings = useSelector(selectWarnings);
  const allErrors = useSelector(selectErrors);
  const { t } = useTranslation();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const sequence = getTake3DSequence(take);
  const sequenceWarnings = sequence ? allWarnings[sequence.id] ?? [] : [];
  const sequenceErrors = sequence ? allErrors[sequence.id] ?? [] : [];
  const hasSequenceWarnings = sequenceWarnings.length > 0;
  const hasSequenceErrors = sequenceErrors.length > 0;

  const onToggleExpand = () => {
    const cpy: Take3D = { ...take, isExpanded: !take.isExpanded };
    dispatch(updateSelectedTake(cpy));
  };

  const onClick = () => {
    dispatch(setSelectedTake(take.id));
  };

  const DeleteBubble = () => {
    deleteTake3D(take);
  };

  const icon = (
    <div
      className={classNames({
        "flex my-auto text-[22px]": true,
        "text-creator-primary": isSelected,
        "text-creator-text-sub": !isSelected,
      })}
    >
      <MovieCreationOutlinedIcon className={"my-auto"} fontSize={"inherit"} />
    </div>
  );

  const arrow = (
    <div
      className={classNames({
        "flex my-auto text-[20px] text-creator-text !cursor-pointer": true,
        "transition-transform rotate-180": take.isExpanded,
        "transition-transform rotate-0": !take.isExpanded,
      })}
      onClick={onToggleExpand}
    >
      <KeyboardArrowDownIcon
        className={"my-auto"}
        color={"inherit"}
        fontSize={"inherit"}
      />
    </div>
  );

  const name = (
    <div
      className={classNames({
        "my-auto w-full truncate text-[14px]": true,
        "font-bold text-creator-primary": isSelected && !hasSequenceErrors,
        "font-medium text-creator-text-sub": !isSelected,
        "text-creator-error": hasSequenceErrors,
      })}
    >
      {take.name}
    </div>
  );

  const warningIcon = hasSequenceWarnings ? (
    <div
      data-testid="take-sequence-warning-icon"
      className={"my-auto text-[12px] text-warning-600"}
    >
      <InfoIcon fontSize={"inherit"} />
    </div>
  ) : null;

  const errorIcon = hasSequenceErrors ? (
    <div
      data-testid="take-sequence-error-icon"
      className={"my-auto text-[12px] text-creator-error"}
    >
      <ErrorIcon fontSize={"inherit"} />
    </div>
  ) : null;

  const menu = (
    <div className={"my-auto"}>
      <PiPopoverMenu>
        <></>
        <PiMenuButton
          text={t("delete")}
          onClick={() => setIsDeleteDialogOpen(true)}
        />
      </PiPopoverMenu>
    </div>
  );

  return (
    <InteractionContextMenu>
      <div
        className={"flex gap-[10px] !h-[56px] w-full p-[10px]"}
        onClick={onClick}
        {...props}
      >
        {arrow}
        {icon}
        {warningIcon}
        {errorIcon}
        {name}
        <DisabledInteractionsBadge count={countDisabledInteractions(take)} />
        {menu}
        <PiConfirmationDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={DeleteBubble}
          text={t("delete_take.delete_description")}
          title={t("delete_take.delete_title")}
          confirmText={t("delete_take.confirm_delete")}
          cancelText={t("delete_take.cancel_delete")}
          height="215px"
          confirmButtonVariant="piRed"
        />
      </div>
    </InteractionContextMenu>
  );
});
